import { useEffect, useState, useRef } from "react";

import { css } from "@compiled/react";
import { flattenValues } from "@uniformdev/canvas";
import { UniformRichTextNode } from "@uniformdev/canvas-react";
import { RichTextNode } from "@uniformdev/richtext";
import { useIntl } from "react-intl";
import { UniformConfluenceHeroSingleCard } from "src/uniform/contenttypes/confluenceHeroSingleCard";

import VideoPauseOverlayIcon from "@atlaskit/icon/core/video-pause-overlay";
import VideoPlayOverlayIcon from "@atlaskit/icon/core/video-play-overlay";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Image } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import {
    WCL_GRID_MAX_WIDTH,
    WCL_GRID_PADDING_LG,
    WCL_GRID_PADDING_SM
} from "@/libraries/design/foundations/grid/src/base";

export type ConfluenceAnimatedHeroBackgroundColor =
    | "blue100"
    | "primaryBlack"
    | "light100";

const backgroundColorMap: Record<
    ConfluenceAnimatedHeroBackgroundColor,
    string
> = {
    blue100: wacToken("color.extended.blue100"),
    primaryBlack: wacToken("color.primary.black"),
    light100: wacToken("color.neutral.light100")
};

export interface UniformConfluenceAnimatedHeroProps {
    confluenceHeroCards: UniformConfluenceHeroSingleCard[];
    imagesWidth?: string;
    imagesHeight?: string;
    flattenData?: boolean;
    backgroundColor?: ConfluenceAnimatedHeroBackgroundColor;
}

const sectionCss = css({
    transition: "clip-path 0.6s ease-in-out",
    [`&.outview`]: {
        clipPath:
            "polygon(0 -0.5px, 100% -0.5px, 100% calc(100% + (var(--moveup) * 0.75 )  ), 0 calc(100% + (var(--moveup) * 0.3)  ))",
        [`@media (prefers-reduced-motion: reduce)`]: {
            clipPath:
                "polygon(0 -0.5px, 100% -0.5px, 100% calc(100% - 50px), 0 100%)"
        }
    },
    [`&.inview`]: {
        clipPath:
            "polygon(0 -0.5px, 100% -0.5px, 100% calc(100% - 50px), 0 100%)"
    }
});

const cardContainerCss = css({
    "display": "grid",
    "width": "100%",
    "margin": "auto",
    "maxWidth": "100%",
    "paddingInlineStart": `${WCL_GRID_PADDING_SM}px`,
    "paddingInlineEnd": `${WCL_GRID_PADDING_SM}px`,
    "gap": "var(--cl-gap)",
    "--cl-gap": "24px",
    [media.only.sm]: {
        paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
        paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`
    },
    [media.above.md]: {
        "paddingInlineStart": `${WCL_GRID_PADDING_LG}px`,
        "paddingInlineEnd": `${WCL_GRID_PADDING_LG}px`,
        "maxWidth": `${WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2}px`,
        "--cl-gap": "32px"
    },
    [media.above.sm]: {
        gridTemplateColumns: "repeat(3, 1fr)",
        paddingBlockStart: token("space.200")
    },
    "justifyContent": "center",
    [`&.inview`]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`.single-hero-card`]: {
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            [`img, .video-container`]: {
                transform: "scale(1) translateY(0) rotateZ(0deg)",
                borderRadius: `${wacToken("corner.radius.card")} ${wacToken(
                    "corner.radius.card"
                )} 0 0`
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            [`.card-text`]: {
                filter: "opacity(1)"
            }
        }
    },
    [media.below.sm]: {
        paddingBlockEnd: token("space.800"),
        rowGap: token("space.800"),
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`.single-hero-card`]: {
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            [`img, .video-container`]: {
                transition: "none",
                transform: "none"
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            [`.card-text`]: {
                transition: "none",
                filter: "opacity(1)"
            }
        }
    }
});

const cardCss = css({
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    position: "relative",
    ["--moveout"]: "0px",
    [media.above.lg]: {
        "--moveout": "60px"
    },

    [`&:first-of-type`]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`img, .video-container`]: {
            transformOrigin: "0 0",
            transform:
                "translateY(calc(var(--moveup) + 100px)) translateX(calc(0px - var(--moveout))) scale(1.35) rotateZ(-7deg)"
        }
    },
    [`&:nth-of-type(2)`]: {
        zIndex: 2,
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`img, .video-container`]: {
            transformOrigin: "50% 0",
            transform: "translateY(var(--moveup)) scale(1.76) rotateZ(0deg)"
        }
    },
    [`&:nth-of-type(3)`]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`img, .video-container`]: {
            transformOrigin: "100% 0",
            transform:
                "translateY(calc(var(--moveup) + 100px)) translateX(var(--moveout)) scale(1.35) rotateZ(7deg)"
        }
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [`img, .video-container`]: {
        "aspectRatio": "var(--imagesWidth) / var(--imagesHeight)",
        "width": "100%",
        "height": "auto",
        "overflow": "hidden",
        "margin": "auto auto 0",
        "transition":
            "transform 0.6s ease-in-out, border-radius 0.6s ease-in-out",
        "borderRadius": "10.8px 10.8px 0 0",
        [media.below.sm]: {
            borderRadius: wacToken("corner.radius.card")
        },
        "backgroundColor": wacToken("color.neutral.light0"),
        "boxShadow": token("elevation.shadow.overlay"),
        "@media (prefers-reduced-motion: reduce)": {
            transform: "none !important"
        }
    }
});

const cardTextCss = css({
    "filter": "opacity(0)",
    "margin": "0 0 0.5rem",
    "transition": "filter 0.6s ease-in-out",
    [`&.card-heading`]: {
        fontWeight: "normal"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [`strong`]: {
        fontWeight: 600
    },
    "@media (prefers-reduced-motion: reduce)": {
        filter: "opacity(1)"
    }
});
const cardHeadingCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [`&.card-heading >`]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`h2, .h2, h3, .h3, h4, .h4`]: {
            margin: 0,
            lineHeight: 1.25,
            fontSize: token("space.300"),
            fontWeight: 400
        }
    }
});

const cardBodyTextCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [`&.card-body > p`]: {
        margin: 0,
        fontSize: token("space.200"),
        lineHeight: "1.5rem",
        fontWeight: 400
    }
});

const controlButtonStyles = css({
    cursor: "pointer",
    opacity: 0.4,
    background: "transparent",
    color: wacToken("color.neutral.light0"),
    border: `2px solid`,
    borderRadius: wacToken("corner.radius.pill"),
    height: "30px",
    width: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    zIndex: 4,
    insetInlineEnd: token("space.200"),
    insetBlockEnd: token("space.500"),
    [`&:hover`]: {
        opacity: 1
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [`svg`]: {
        width: "14px"
    }
});

const videoContainerCss = css({
    position: "relative",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [`video`]: {
        objectFit: "cover",
        width: "100%",
        height: "100%"
    }
});

export const ConfluenceSingleHeroCard = ({
    title,
    bodytext,
    image,
    altText
}: {
    title: RichTextNode | { root: RichTextNode };
    bodytext: RichTextNode | { root: RichTextNode };
    image: string;
    altText: string;
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setPlaying] = useState(false);
    const intl = useIntl();
    const prefersReducedMotion =
        typeof window !== "undefined"
            ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
            : false;

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play().catch(() => {
                    // Ignore error likely due to autoplay restrictions or browser power saving
                });
            }
        }
    };

    const handleVideoPlay = () => setPlaying(true);
    const handleVideoPause = () => setPlaying(false);

    return (
        <div className="single-hero-card" css={cardCss}>
            <div className="text-container">
                <div
                    className="card-text card-heading"
                    css={[cardHeadingCss, cardTextCss]}
                >
                    <UniformRichTextNode
                        node={(title["root"] || title) as RichTextNode}
                    />
                </div>
                <div
                    className="card-text card-body"
                    css={[cardBodyTextCss, cardTextCss]}
                >
                    <UniformRichTextNode
                        node={(bodytext["root"] || bodytext) as RichTextNode}
                    />
                </div>
            </div>
            {image.includes(".mp4") ? (
                <div
                    className="imagery video video-container"
                    css={[videoContainerCss]}
                >
                    <video
                        ref={videoRef}
                        src={image}
                        autoPlay={!prefersReducedMotion}
                        muted
                        loop
                        playsInline
                        onPlay={handleVideoPlay}
                        onPause={handleVideoPause}
                        onLoadedMetadata={() => {
                            if (videoRef.current && !prefersReducedMotion) {
                                videoRef.current.play().catch(() => {
                                    // Ignore error likely due to autoplay restrictions or browser power saving
                                });
                                // autoplay by default but if browser restrictions prevent it button will show in play state
                            }
                        }}
                    />
                    <button
                        className="control-button"
                        css={controlButtonStyles}
                        onClick={togglePlay}
                    >
                        {isPlaying ? (
                            <VideoPauseOverlayIcon
                                label={intl.formatMessage({
                                    id: "uniform-video.pause",
                                    defaultMessage: "Pause"
                                })}
                            />
                        ) : (
                            <VideoPlayOverlayIcon
                                label={intl.formatMessage({
                                    id: "uniform-video.play",
                                    defaultMessage: "Play"
                                })}
                            />
                        )}
                    </button>
                </div>
            ) : (
                <Image src={image} alt={altText} />
            )}
        </div>
    );
};

const UniformConfluenceAnimatedHero = ({
    confluenceHeroCards,
    imagesWidth,
    imagesHeight,
    flattenData = true,
    backgroundColor = "blue100"
}: UniformConfluenceAnimatedHeroProps) => {
    if (flattenData) {
        confluenceHeroCards = flattenValues(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            confluenceHeroCards as any
        ) as UniformConfluenceHeroSingleCard[];
    }

    const [inview, setInview] = useState(false);
    const [hasScrolled, setHasScrolled] = useState(false);
    const [moveUpValue, setMoveUpValue] = useState(120);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY <= 50) {
                setInview(false);
                setHasScrolled(false);
                return;
            } else {
                setHasScrolled(true);
            }
        };

        window.addEventListener("scroll", handleScroll);

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    useEffect(() => {
        if (!hasScrolled || !containerRef.current) return;

        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setInview(true);
                    }
                });
            },
            { threshold: 0.5 }
        );

        io.observe(containerRef.current);

        return () => {
            io.disconnect();
        };
    }, [hasScrolled]);

    useEffect(() => {
        const updateMoveUpValue = () => {
            if (!containerRef.current) return;

            const textContainers =
                containerRef.current.querySelectorAll(".text-container");
            let maxHeight = 0;

            textContainers.forEach((container) => {
                const height = (container as HTMLElement).offsetHeight;
                maxHeight = Math.max(maxHeight, height);
            });

            const calculatedMoveUp = maxHeight + 16;
            setMoveUpValue(calculatedMoveUp);
        };

        const timeoutId = setTimeout(updateMoveUpValue, 100);
        document?.fonts?.ready?.then(updateMoveUpValue);
        window.addEventListener("resize", updateMoveUpValue);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener("resize", updateMoveUpValue);
        };
    }, []);

    return (
        <section
            className={"scroll-hero" + (inview ? " inview" : " outview")}
            css={sectionCss}
            style={
                {
                    "--moveup": `-${moveUpValue}px`,
                    "--imagesWidth": imagesWidth,
                    "--imagesHeight": imagesHeight,
                    "background": backgroundColorMap[backgroundColor]
                } as React.CSSProperties
            }
        >
            <div
                ref={containerRef}
                className={inview ? "inview" : "outview"}
                css={cardContainerCss}
            >
                {confluenceHeroCards.map(
                    (card: UniformConfluenceHeroSingleCard, index) => (
                        <ConfluenceSingleHeroCard
                            key={index}
                            title={card.cardTitle}
                            bodytext={card.cardDescription}
                            image={card.cardImageOrVideo}
                            altText={card.cardImageAlt}
                        />
                    )
                )}
            </div>
        </section>
    );
};

export default UniformConfluenceAnimatedHero;
